import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { Container, useTick } from '@pixi/react';
import { useMemo } from 'react';
import { useCallback, useRef } from 'react';
import { loadedBombSpriteSheets } from '../../../bombs/bombLoaderUtils';
import { addPlayerAnimation } from '../../../redux/actions/animationsActions';
import { removeBomb, thunkAddBombSplat } from '../../../redux/actions/bombsActions';
import { useAppDispatch } from '../../../redux/store';
import { MINIMUM_PLAYER_ANIMATION_DURATION_MS } from '../../../utils/playerAnimationUtils';
import { getVelocityComponentsAtTime, getXAtTime, getYAtTime } from '../../../utils/trajectoryUtils';
import { assertUnreachable } from '../../../utils/tsUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { InternalAnimatedSprite } from '../pixiComponents/internalAnimatedSprite';
const TRAJECTORY_SPEED_FACTOR = 0.65;
const MAX_BOMB_ANIMATION_MS = 8000;
export const BombDisplay = ({ bomb, bombProperties, baseBombScaleFactor }) => {
    const dispatch = useAppDispatch();
    const containerRef = useRef(null);
    const rotationContainerRef = useRef(null);
    const animate = useCallback(() => {
        if (!containerRef.current || !rotationContainerRef.current) {
            return;
        }
        const { id, startTimestampMs, trajectory, fromX, fromY, toSeatIndex } = bomb;
        const nowMs = performance.now();
        if (nowMs > startTimestampMs + MAX_BOMB_ANIMATION_MS) {
            dispatch(removeBomb(id));
            return;
        }
        const elapsedSecs = (nowMs - startTimestampMs) / 1000;
        const elapsedTime = TRAJECTORY_SPEED_FACTOR * elapsedSecs;
        const newX = getXAtTime(elapsedTime, trajectory.velocity, fromX, trajectory.radians, trajectory.isGoingRight);
        const newY = getYAtTime(elapsedTime, trajectory.velocity, fromY, trajectory.radians);
        const { velocityX, velocityY, radians } = getVelocityComponentsAtTime(elapsedTime, trajectory.velocity, trajectory.radians, trajectory.isGoingRight);
        if (velocityY > 0) {
            // traveling down, do collision detection
            // const distanceFromTargetCenter = Math.sqrt(Math.pow(newX - bomb.toX, 2) + Math.pow(newY - bomb.toY, 2))
            if (newY >= bomb.toY) {
                dispatch(thunkAddBombSplat({
                    id: -1,
                    bombType: bomb.bombType,
                    seatIndex: toSeatIndex,
                    x: newX,
                    y: newY,
                    radians,
                    startTimestampMs: performance.now()
                }));
                dispatch(removeBomb(id));
                dispatch(addPlayerAnimation(bomb.toSeatIndex, {
                    type: 'bomb-bounce',
                    velocityX,
                    velocityY,
                    strength: bombProperties.strength,
                    durationMs: Math.max(1000, MINIMUM_PLAYER_ANIMATION_DURATION_MS)
                }));
                return;
            }
        }
        containerRef.current.x = newX;
        containerRef.current.y = newY;
        switch (bombProperties.orientationType) {
            case 'spinning': {
                if (rotationContainerRef.current.angle !== undefined) {
                    rotationContainerRef.current.angle += bomb.trajectory.isGoingRight ? 6 : -6;
                }
                break;
            }
            case 'path': {
                containerRef.current.rotation = radians;
                break;
            }
            case 'none': {
                break;
            }
            default: {
                assertUnreachable(bombProperties.orientationType);
            }
        }
        containerRef.current.visible = true;
    }, [bomb, bombProperties.orientationType, bombProperties.strength, dispatch]);
    useTick(animate);
    const textures = useMemo(() => {
        const bombSpritesheets = loadedBombSpriteSheets[bomb.bombType];
        if (!bombSpritesheets) {
            return;
        }
        // this bomb's animation (multiple textures/frames) may exist, or it could be just a single texture/frame, so detect which it is:
        const animations = bombSpritesheets.bomb.animations;
        if (animations && Object.keys(animations).length) {
            return animations[Object.keys(animations)[0]];
        }
        const textures = bombSpritesheets.bomb.textures;
        return textures && [textures[Object.keys(textures)[0]]];
    }, [bomb.bombType]);
    if (!textures) {
        return null;
    }
    const pixiData = isTestEnvironment()
        ? {
            type: 'bomb',
            bombType: bomb.bombType,
            id: bomb.id,
            fromX: bomb.fromX,
            fromY: bomb.fromY,
            fromSeatIndex: bomb.fromSeatIndex,
            toX: bomb.toX,
            toY: bomb.toY,
            toSeatIndex: bomb.toSeatIndex,
            initialRadians: bomb.trajectory.radians,
            initialVelocity: bomb.trajectory.velocity
        }
        : null;
    const scale = baseBombScaleFactor;
    return (_jsx(Container, { ref: containerRef, x: 0, y: 0, visible: false, "pixi-data": pixiData, children: _jsx(Container, { scale: scale, ref: rotationContainerRef, children: _jsx(InternalAnimatedSprite, { textures: textures, animationSpeed: 0.5, loop: true }) }) }));
};
